import _ from 'lodash';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useViewer } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Callout, Modal, Popover, Portal, Tooltip } from '@treams/treams-ui';

import VoiceMemoButton from './VoiceMemoButton';
import VoiceMemoOverlay from './VoiceMemoOverlay';
import VoiceMemoPlayback from './VoiceMemoPlayback';
import VoiceMemoTransition from './VoiceMemoTransition';

type VoiceMemoDraft = { blob: Blob; samples: number[]; sampleRate: number };

type Props = {
	voiceMemo?: VoiceMemoDraft | graphql.VoiceMemo;
	onChange: (voiceMemo?: VoiceMemoDraft) => void;
	startOnMount?: boolean;
};

export default function VoiceMemoTarget({
	voiceMemo,
	onChange,
	children,
	startOnMount = false,
}: PropsWithChildren<Props>) {
	const enabled = !!useViewer();
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement | null>(null);
	const [active, setActive] = useState(false);
	const [error, setError] = useState(false);
	const [bounds, setBounds] = useState<
		Record<'top' | 'left' | 'width' | 'height', number> | undefined
	>();
	const [, setRandom] = useState(Math.random());

	const lastVoiceMemo = useRef(voiceMemo);

	if (voiceMemo) {
		lastVoiceMemo.current = voiceMemo;
	}

	const hidden = !!(lastVoiceMemo.current || bounds);

	const onStart = () => {
		setActive(true);
		onMemoButtonClick();
	};

	const onMemoButtonClick = () => {
		setError(false);
		setBounds(_.pick(ref.current!.getBoundingClientRect(), ['top', 'left', 'width', 'height']));
	};

	const onFinish = (voiceMemo: VoiceMemoDraft) => {
		onChange(voiceMemo);
		setBounds(undefined);
		setActive(false);
	};

	const onError = () => {
		setError(true);
		setBounds(undefined);
		setActive(false);
	};

	useEffect(() => {
		if (startOnMount) {
			setTimeout(() => {
				onStart();
			}, 200);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startOnMount]);

	return (
		<>
			<div
				ref={ref}
				style={{
					position: 'relative',
				}}
			>
				{enabled && (
					<div
						style={{
							position: 'absolute',
							left: -40,
							opacity: hidden ? 0 : 1,
							pointerEvents: hidden ? 'none' : undefined,
						}}
					>
						<Popover.Target
							visible={error}
							contents={
								<Box>
									<Box p={3} maxWidth={400}>
										<Callout color="danger">{t('voiceMemo.error.message')}</Callout>
									</Box>
									<Modal.Footer>
										<Button
											type="primary"
											small
											title={t('voiceMemo.error.dismiss')}
											onClick={() => setError(false)}
										/>
									</Modal.Footer>
								</Box>
							}
							onDismiss={() => setError(false)}
						>
							<Tooltip content={t('voiceMemo.record.tooltip')} position="right" portal>
								<VoiceMemoButton
									onClick={() => {
										setError(false);
										setBounds(
											_.pick(ref.current!.getBoundingClientRect(), [
												'top',
												'left',
												'width',
												'height',
											]),
										);
									}}
								/>
							</Tooltip>
						</Popover.Target>
					</div>
				)}
				<div
					style={{
						opacity: active || voiceMemo ? 0 : 1,
						transformOrigin: '-24px 16px',
						transform: active || voiceMemo ? 'scale(0, 0)' : 'none',
						transition: '1s cubic-bezier(0, 1, 0, 1) all',
					}}
				>
					{children}
				</div>
				{lastVoiceMemo.current && (
					<div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}>
						<VoiceMemoTransition status={voiceMemo ? 'expanded' : 'collapsed'}>
							<VoiceMemoPlayback
								fromRecording
								source={
									'blob' in lastVoiceMemo.current
										? lastVoiceMemo.current.blob
										: lastVoiceMemo.current.asset.source.url
								}
								samples={lastVoiceMemo.current.samples}
								onCancel={() => {
									onChange();

									setActive(false);

									setTimeout(() => {
										lastVoiceMemo.current = undefined;
										setRandom(Math.random());
									}, 1000);
								}}
							/>
						</VoiceMemoTransition>
					</div>
				)}
			</div>
			{bounds && !voiceMemo && (
				<Portal>
					<Box style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
					<Box style={{ position: 'absolute', ...bounds, top: bounds.top + window.scrollY }}>
						<VoiceMemoOverlay onStart={onStart} onError={onError} onFinish={onFinish} />
					</Box>
				</Portal>
			)}
		</>
	);
}
